// src/pages/CharacterEditPage.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getCharacter, updateCharacter, updateCharacterAvatar } from '../services/characterService';
import { Character } from '../types/character';
import { PhotoIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import XorPixViewer from '../components/xorpix/XorPixViewer';
import { CHARACTER_TAGS } from '../constants/characterTags';

const CharacterEditPage: React.FC = () => {
  const { characterId } = useParams<{ characterId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Form state
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [cardTheme, setCardTheme] = useState<'dark' | 'light'>('dark');
  const [themeColors, setThemeColors] = useState<string[]>(['#6366f1', '#ec4899', '#f59e0b']);
  const [isPublic, setIsPublic] = useState(false);
  const [allowFanart, setAllowFanart] = useState(true);

  // Image state
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [character, setCharacter] = useState<Character | null>(null);

  // Fetch character data
  const fetchCharacter = useCallback(async () => {
    if (!characterId || !user) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await getCharacter(characterId);
      if (data) {
        // Check ownership
        if (data.ownerId !== user.uid) {
          setError('このキャラクターを編集する権限がありません');
          return;
        }

        setCharacter(data);
        setName(data.name || '');
        setBio(data.bio || '');
        setSelectedTags(data.tags || []);
        setCardTheme(data.cardTheme || 'dark');
        setThemeColors(data.themeColors || ['#6366f1', '#ec4899', '#f59e0b']);
        setIsPublic(data.isPublic || false);
        setAllowFanart(data.allowFanart !== false);
        // XOR-Pix画像がある場合はimagePreviewをnullにしてXorPixViewerを使用
        // レガシー画像のみの場合はavatarUrl/headerImageUrlを使用
        const hasXorPix = data.noise1Url && data.noise2Url && data.rid;
        setImagePreview(hasXorPix ? null : (data.thumbnailUrl || data.avatarUrl || data.headerImageUrl || null));
      } else {
        setError('キャラクターが見つかりません');
      }
    } catch (err) {
      console.error('Error fetching character:', err);
      setError('キャラクターの読み込みに失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, [characterId, user]);

  useEffect(() => {
    if (user) {
      fetchCharacter();
    }
  }, [fetchCharacter, user]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const toggleTag = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const updateThemeColor = (index: number, color: string) => {
    setThemeColors(prev => {
      const newColors = [...prev];
      newColors[index] = color;
      return newColors;
    });
  };

  // Handle save
  const handleSave = async () => {
    if (!characterId || !user || isSaving || !name.trim()) return;

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Update character data
      await updateCharacter(characterId, {
        name: name.trim(),
        bio: bio.trim(),
        tags: selectedTags,
        cardTheme,
        themeColors,
        isPublic,
        allowFanart,
      });

      // Update avatar if a new image was selected
      if (imageFile) {
        await updateCharacterAvatar(characterId, imageFile);
      }

      setSuccessMessage('保存しました');

      // Navigate back to character page after short delay
      setTimeout(() => {
        navigate(`/character/${characterId}`);
      }, 1000);
    } catch (err) {
      console.error('Error saving character:', err);
      setError('保存に失敗しました');
    } finally {
      setIsSaving(false);
    }
  };

  // Get display name and username from user
  const displayName = user?.displayName || 'Creator';
  const username = user?.email?.split('@')[0] || 'username';

  // Not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/60 mb-4">ログインが必要です</p>
          <Link to="/login" className="text-white hover:underline">
            ログイン
          </Link>
        </div>
      </div>
    );
  }

  // Loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  // Error
  if (error && !character) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <Link to="/" className="text-white hover:underline">
            トップへ戻る
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link
            to={`/character/${characterId}`}
            className="p-2 -ml-2 rounded-full hover:bg-white/10 transition-colors"
          >
            <ChevronLeftIcon className="w-6 h-6 text-white" />
          </Link>
          <h1 className="font-bold text-white">ORIFOLIを編集</h1>
          <div className="w-10" />
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Success/Error Messages */}
        {successMessage && (
          <div className="mb-6 bg-green-500/20 text-green-400 px-4 py-3 rounded-xl text-center">
            {successMessage}
          </div>
        )}
        {error && (
          <div className="mb-6 bg-red-500/20 text-red-400 px-4 py-3 rounded-xl text-center">
            {error}
          </div>
        )}

        <div className="flex flex-col md:flex-row gap-6 items-center md:items-start justify-center">
          {/* Card Preview + Theme */}
          <div className="flex flex-col items-center">
            <div className="bg-white p-2 rounded-2xl shadow-2xl">
              <div
                className="relative w-72 aspect-[2/3] rounded-xl overflow-hidden cursor-pointer group"
                onClick={() => imageInputRef.current?.click()}
              >
                {/* Full Bleed Image */}
                {imageFile && imagePreview ? (
                  // 新しい画像が選択された場合はプレビュー表示
                  <img
                    src={imagePreview}
                    alt="Character"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : character?.noise1Url && character?.noise2Url && character?.rid ? (
                  // XOR-Pix暗号化画像がある場合
                  <XorPixViewer
                    noise1Url={character.noise1Url}
                    noise2Url={character.noise2Url}
                    rid={character.rid}
                    width={character.imageWidth || 800}
                    height={character.imageHeight || 1200}
                    characterId={character.id}
                    fallbackNoise1Url={character.fallbackNoise1Url}
                    fallbackNoise2Url={character.fallbackNoise2Url}
                    fitMode="cover"
                    className="absolute inset-0 w-full h-full"
                  />
                ) : imagePreview ? (
                  // レガシー画像（サムネイル/アバター）
                  <img
                    src={imagePreview}
                    alt="Character"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center">
                    <PhotoIcon className="w-12 h-12 text-gray-300 group-hover:text-gray-400 transition-colors" />
                    <span className="text-gray-400 text-sm mt-2">タップして画像を選択</span>
                  </div>
                )}

                {/* Top Overlay - Logo */}
                <div className={`absolute top-0 left-0 right-0 pt-4 pb-8 px-4 bg-gradient-to-b ${
                  cardTheme === 'dark' ? 'from-black/40 to-transparent' : 'from-white/60 to-transparent'
                }`}>
                  <span className={`text-[10px] font-medium tracking-[0.2em] uppercase ${
                    cardTheme === 'dark' ? 'text-white/80' : 'text-black/70'
                  }`}>
                    ORIFOLI
                  </span>
                </div>

                {/* Bottom Overlay - Character Info */}
                <div className={`absolute bottom-0 left-0 right-0 pt-20 pb-4 px-4 bg-gradient-to-t ${
                  cardTheme === 'dark' ? 'from-black/60 via-black/40 to-transparent' : 'from-white/90 via-white/60 to-transparent'
                }`}>
                  {/* Character Name */}
                  <p className={`text-xl font-semibold tracking-tight line-clamp-2 ${
                    cardTheme === 'dark' ? 'text-white' : 'text-gray-500'
                  }`} style={{ lineHeight: '1.3' }}>
                    {name || <span className={cardTheme === 'dark' ? 'text-white/40' : 'text-gray-400'}>Character Name</span>}
                  </p>

                  {/* Bio */}
                  {bio && (
                    <p className={`text-[10px] mt-0.5 line-clamp-2 leading-relaxed ${
                      cardTheme === 'dark' ? 'text-white/60' : 'text-gray-600'
                    }`}>
                      {bio}
                    </p>
                  )}

                  {/* Tags Row */}
                  <div className="flex gap-1 mt-0.5 min-h-[14px]">
                    {selectedTags.slice(0, 3).map(tag => (
                      <span
                        key={tag}
                        className={`text-[9px] ${cardTheme === 'dark' ? 'text-white/50' : 'text-gray-500'}`}
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>

                  {/* Divider */}
                  <div className={`border-t my-3 ${cardTheme === 'dark' ? 'border-white/20' : 'border-gray-300'}`} />

                  {/* Creator Info */}
                  <div className="flex items-center gap-2">
                    <div className={`w-5 h-5 rounded-full overflow-hidden flex-shrink-0 ${
                      cardTheme === 'dark' ? 'bg-white/20' : 'bg-gray-200'
                    }`}>
                      {user?.photoURL ? (
                        <img
                          src={user.photoURL}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className={`w-full h-full bg-gradient-to-br ${
                          cardTheme === 'dark' ? 'from-white/30 to-white/10' : 'from-gray-300 to-gray-200'
                        }`} />
                      )}
                    </div>
                    <p className={`text-[9px] tracking-wide truncate ${
                      cardTheme === 'dark' ? 'text-white/50' : 'text-gray-500'
                    }`}>
                      <span className={cardTheme === 'dark' ? 'text-white/30' : 'text-gray-400'}>CREATOR/</span>{displayName} <span className={cardTheme === 'dark' ? 'text-white/30' : 'text-gray-400'}>@{username}</span>
                    </p>
                  </div>
                </div>

                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </div>
            </div>

            {/* Theme Selection - Below Card */}
            <div className="w-72 mt-3 flex gap-2">
              <button
                onClick={() => setCardTheme('dark')}
                className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
                  cardTheme === 'dark'
                    ? 'bg-white text-gray-900'
                    : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
              >
                Dark
              </button>
              <button
                onClick={() => setCardTheme('light')}
                className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
                  cardTheme === 'light'
                    ? 'bg-white text-gray-900'
                    : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
              >
                Light
              </button>
            </div>
          </div>

          {/* Controls Panel */}
          <div className="w-72 md:w-80 space-y-3">
            {/* Name Input */}
            <div className="bg-white/20 backdrop-blur-md rounded-2xl p-4">
              <h3 className="text-xs font-medium text-white mb-2 tracking-wide uppercase">Name</h3>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="キャラクター名"
                className="w-full bg-white/90 text-black text-sm rounded-lg px-3 py-2 placeholder-gray-500 outline-none focus:bg-white transition-colors"
                maxLength={30}
              />
            </div>

            {/* Bio Input */}
            <div className="bg-white/20 backdrop-blur-md rounded-2xl p-4">
              <h3 className="text-xs font-medium text-white mb-2 tracking-wide uppercase">Bio</h3>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="キャラクターの紹介..."
                className="w-full bg-white/90 text-black text-xs rounded-lg p-2 placeholder-gray-500 outline-none focus:bg-white transition-colors resize-none"
                rows={3}
                maxLength={100}
              />
            </div>

            {/* Tag Selection */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4">
              <h3 className="text-xs font-medium text-white/60 mb-3 tracking-wide uppercase">Tags</h3>

              {/* Genre (character type) */}
              <div className="mb-3">
                <p className="text-[10px] text-white/40 mb-1.5 uppercase tracking-wider">{CHARACTER_TAGS.genre.label}</p>
                <div className="flex flex-wrap gap-1.5">
                  {CHARACTER_TAGS.genre.options.map(option => (
                    <button
                      key={option.value}
                      onClick={() => toggleTag(option.value)}
                      className={`px-2.5 py-1 rounded-full text-xs transition-all ${
                        selectedTags.includes(option.value)
                          ? 'bg-white text-gray-900'
                          : 'bg-white/10 text-white/70 hover:bg-white/20'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Personality */}
              <div className="mb-3">
                <p className="text-[10px] text-white/40 mb-1.5 uppercase tracking-wider">{CHARACTER_TAGS.personality.label}</p>
                <div className="flex flex-wrap gap-1.5">
                  {CHARACTER_TAGS.personality.options.map(option => (
                    <button
                      key={option.value}
                      onClick={() => toggleTag(option.value)}
                      className={`px-2.5 py-1 rounded-full text-xs transition-all ${
                        selectedTags.includes(option.value)
                          ? 'bg-white text-gray-900'
                          : 'bg-white/10 text-white/70 hover:bg-white/20'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Worldview */}
              <div>
                <p className="text-[10px] text-white/40 mb-1.5 uppercase tracking-wider">{CHARACTER_TAGS.worldview.label}</p>
                <div className="flex flex-wrap gap-1.5">
                  {CHARACTER_TAGS.worldview.options.map(option => (
                    <button
                      key={option.value}
                      onClick={() => toggleTag(option.value)}
                      className={`px-2.5 py-1 rounded-full text-xs transition-all ${
                        selectedTags.includes(option.value)
                          ? 'bg-white text-gray-900'
                          : 'bg-white/10 text-white/70 hover:bg-white/20'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Theme Colors */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4">
              <h3 className="text-xs font-medium text-white/60 mb-3 tracking-wide uppercase">Theme Colors</h3>
              <p className="text-[10px] text-white/40 mb-3">キャラクターのイメージカラーを3色選択</p>
              <div className="flex gap-3 justify-center">
                {[0, 1, 2].map((index) => (
                  <div key={index} className="flex flex-col items-center gap-2">
                    <label className="relative cursor-pointer group">
                      <div
                        className="w-12 h-12 rounded-xl shadow-lg transition-transform group-hover:scale-105 border-2 border-white/20"
                        style={{ backgroundColor: themeColors[index] || '#888888' }}
                      />
                      <input
                        type="color"
                        value={themeColors[index] || '#888888'}
                        onChange={(e) => updateThemeColor(index, e.target.value)}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                    </label>
                    <span className="text-[10px] text-white/40 uppercase">
                      {index === 0 ? 'Primary' : index === 1 ? 'Secondary' : 'Accent'}
                    </span>
                  </div>
                ))}
              </div>
              {/* Color Preview Bar */}
              <div className="mt-4 h-2 rounded-full overflow-hidden flex">
                <div className="flex-1" style={{ backgroundColor: themeColors[0] }} />
                <div className="flex-1" style={{ backgroundColor: themeColors[1] }} />
                <div className="flex-1" style={{ backgroundColor: themeColors[2] }} />
              </div>
            </div>

            {/* Settings */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 space-y-3">
              <h3 className="text-xs font-medium text-white/60 mb-2 tracking-wide uppercase">Settings</h3>

              {/* isPublic */}
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <p className="text-sm font-medium text-white">公開設定</p>
                  <p className="text-[10px] text-white/40">探索タブで他のユーザーに表示</p>
                </div>
                <div className={`w-10 h-6 rounded-full p-1 transition-colors ${isPublic ? 'bg-green-500' : 'bg-white/20'}`}>
                  <div className={`w-4 h-4 rounded-full bg-white transition-transform ${isPublic ? 'translate-x-4' : ''}`} />
                </div>
                <input
                  type="checkbox"
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  className="hidden"
                />
              </label>

              {/* allowFanart */}
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <p className="text-sm font-medium text-white">ファンアート許可</p>
                  <p className="text-[10px] text-white/40">他ユーザーからの投稿を許可</p>
                </div>
                <div className={`w-10 h-6 rounded-full p-1 transition-colors ${allowFanart ? 'bg-green-500' : 'bg-white/20'}`}>
                  <div className={`w-4 h-4 rounded-full bg-white transition-transform ${allowFanart ? 'translate-x-4' : ''}`} />
                </div>
                <input
                  type="checkbox"
                  checked={allowFanart}
                  onChange={(e) => setAllowFanart(e.target.checked)}
                  className="hidden"
                />
              </label>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex flex-col items-center gap-4">
          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={isSaving || !name.trim()}
            className="bg-white text-gray-900 px-12 py-3 rounded-xl text-sm font-medium hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-900"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                保存中...
              </span>
            ) : (
              '保存する'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CharacterEditPage;
